import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/function-management.css";
import {
  getFunctionsPaginated,
  deleteFunction,
} from "../services/functionService";
import ConfirmModal from "../components/ui/ConfirmModal";
import { useAppContext } from "../context/AppContext";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const FunctionManagementPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastLoadKeyRef = useRef("");
  const { setActiveSection, setFunctionId, setFunctionFormMode } =
    useAppContext();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get("page") || 1);
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [limit, setLimit] = useState(() => {
    const l = Number(searchParams.get("limit") || 10);
    return Number.isInteger(l) && l > 0 ? l : 10;
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    name: "",
    isBulk: false,
    count: 0,
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  // Multiple selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getFunctionsPaginated({
        page,
        limit,
        search: debouncedSearch,
      });
      setList(Array.isArray(result?.data) ? result.data : []);
      setPagination(
        result?.meta || {
          total: 0,
          page: Number(page) || 1,
          limit: Number(limit) || 10,
          totalPages: 1,
        },
      );
    } catch (e) {
      toast.error(e?.message || "Failed to load functions");
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
      setPage(1);
    }, 450);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const pageStr = String(page);
    const limitStr = String(limit);
    const searchStr = String(searchInput || "").trim();

    if (params.get("page") !== pageStr) params.set("page", pageStr);
    if (params.get("limit") !== limitStr) params.set("limit", limitStr);
    if (searchStr) params.set("search", searchStr);
    else params.delete("search");

    const current = searchParams.toString();
    const next = params.toString();
    if (current !== next) {
      setSearchParams(params, { replace: true });
    }
  }, [page, limit, searchInput, searchParams, setSearchParams]);

  useEffect(() => {
    const key = `${page}:${limit}:${debouncedSearch}`;
    if (lastLoadKeyRef.current === key) return;
    lastLoadKeyRef.current = key;
    refresh();
  }, [page, limit, debouncedSearch, refresh]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // When returning from create/edit, allow those pages to request a refresh
  useEffect(() => {
    const needs = localStorage.getItem("functionsNeedsRefresh");
    if (needs === "1") {
      localStorage.removeItem("functionsNeedsRefresh");
      refresh();
    }
  }, [refresh]);

  useEffect(() => {
    const onFocus = () => {
      const needs = localStorage.getItem("functionsNeedsRefresh");
      if (needs === "1") {
        localStorage.removeItem("functionsNeedsRefresh");
        refresh();
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const filtered = useMemo(() => list || [], [list]);

  const goView = (id) => {
    try {
      localStorage.setItem("activeFunctionId", String(id));
    } catch {}
    setFunctionId(id);
    setFunctionFormMode("view");
    setActiveSection("function-detail", { functionId: id });
  };
  const goEdit = (id) => {
    try {
      localStorage.setItem("activeFunctionId", String(id));
    } catch {}
    setFunctionId(id);
    setFunctionFormMode("edit");
    setActiveSection("function-edit", { functionId: id });
  };
  const goCreate = () => {
    setFunctionId(null);
    setFunctionFormMode("create");
    setActiveSection("function-create");
    try {
      localStorage.removeItem("activeFunctionId");
    } catch {}
  };

  const requestDelete = (id, name) => {
    setConfirmDelete({ open: true, id, name: name || "this function", isBulk: false, count: 0 });
  };

  const onDeleteConfirmed = async () => {
    const { id, name } = confirmDelete;
    if (!id) {
      setConfirmDelete({ open: false, id: null, name: "", isBulk: false, count: 0 });
      return;
    }
    try {
      setConfirmDelete((s) => ({ ...s, open: false }));
      setList((prev) => prev.filter((f) => f.function_id !== id));
      await deleteFunction(id);
      toast.success(`Function "${name}" has been deleted successfully!`);
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Failed to delete function");
      await refresh();
    } finally {
      setConfirmDelete({ open: false, id: null, name: "", isBulk: false, count: 0 });
    }
  };

  const openBulkDeleteConfirm = () => {
    if (selectedIds.length === 0) return;
    setConfirmDelete({ open: true, id: null, name: "", isBulk: true, count: selectedIds.length });
  };

  const confirmBulkDelete = async () => {
    try {
      setBulkDeleting(true);
      setConfirmDelete({ open: false, id: null, name: "", isBulk: false, count: 0 });
      for (const id of selectedIds) {
        await deleteFunction(id);
      }
      toast.success(
        `${selectedIds.length} function${
          selectedIds.length > 1 ? "s" : ""
        } deleted successfully!`,
      );
      setSelectedIds([]);
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Failed to delete some functions");
      await refresh();
    } finally {
      setBulkDeleting(false);
    }
  };

  // Multiple selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map((f) => f.function_id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ?
        prev.filter((selectedId) => selectedId !== id)
      : [...prev, id],
    );
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const currentPage = Math.min(
    Math.max(1, Number(pagination.page) || page),
    totalPages,
  );
  const totalRows = Number(pagination.total) || 0;
  const pageSize = Number(pagination.limit) || limit;
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo =
    totalRows === 0 ? 0 : Math.min(currentPage * pageSize, totalRows);

  const visiblePageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3, "ellipsis", totalPages];
    }

    if (currentPage === 3) {
      return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage === totalPages - 2) {
      return [
        1,
        "ellipsis",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  }, [totalPages, currentPage]);

  const limitOptions = useMemo(() => {
    const base = [5, 10, 20, 50];
    const current = Number(limit);
    const all =
      Number.isInteger(current) && current > 0 ? [...base, current] : base;
    return Array.from(new Set(all)).sort((a, b) => a - b);
  }, [limit]);

  return (
    <div className="page-container function-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Function Management</h2>
            <p className="page-subtitle">
              Browse functions. Use actions to view or edit. Create opens a
              dedicated page.
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search by name, code, company, parent"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button
                className="secondary-btn sm"
                onClick={() => setSearchInput("")}
              >
                Clear
              </button>
            </div>
            <button className="primary-btn" onClick={goCreate}>
              + Create Function
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="function-pagination-top">
          <label htmlFor="function-limit" className="function-pagination-label">
            Rows per page:
          </label>
          <select
            id="function-limit"
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value) || 10;
              setLimit(next);
              setPage(1);
            }}
          >
            {limitOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {selectedIds.length > 0 && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "1rem 1.25rem",
              background: "#eff6ff",
              borderRadius: "0.75rem",
              border: "1px solid #bfdbfe",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <span style={{ fontWeight: "600", color: "#1e40af", fontSize: "0.9375rem" }}>
                {selectedIds.length} function{selectedIds.length > 1 ? "s" : ""} selected
              </span>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button
                  className="danger-btn sm"
                  onClick={openBulkDeleteConfirm}
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? "Deleting..." : "Delete Selected"}
                </button>
                <button className="secondary-btn sm" onClick={clearSelection}>
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}
        {loading ?
          <div className="no-results">Loading functions...</div>
        : <div className="roles-table">
            <div className="roles-table-header">
              <div className="cell checkbox">
                <input
                  type="checkbox"
                  checked={
                    selectedIds.length > 0 &&
                    selectedIds.length === filtered.length
                  }
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="cell">Function Name</div>
              <div className="cell">Function Code</div>
              <div className="cell">Company</div>
              <div className="cell">Parent Name</div>
              <div className="cell actions">Actions</div>
            </div>

            {filtered.length === 0 ?
              <div className="no-results">No functions found</div>
            : filtered.map((f) => (
                <div
                  key={f.function_id}
                  className={`roles-table-row ${
                    selectedIds.includes(f.function_id) ? "selected" : ""
                  }`}
                >
                  <div className="cell checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(f.function_id)}
                      onChange={() => toggleSelect(f.function_id)}
                    />
                  </div>
                  <div className="cell">{f.name || "-"}</div>
                  <div className="cell">
                    {f.function_code || f.functionCode || "-"}
                  </div>
                  <div className="cell">
                    {f.company_name || f.company?.name || "-"}
                  </div>
                  <div className="cell">
                    {f.parent_name || f.parentFunction?.name || "-"}
                  </div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === f.function_id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === f.function_id) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(f.function_id);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === f.function_id && (
                        <div
                          className={`action-dropdown-menu${
                            dropdownDirection === "up" ? " dropdown-up" : ""
                          }`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              goView(f.function_id);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              goEdit(f.function_id);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              requestDelete(f.function_id, f.name);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        }

        <ConfirmModal
          open={confirmDelete.open}
          title="Confirm Delete"
          message={
            confirmDelete.isBulk ?
              `Are you sure you want to delete ${confirmDelete.count} function${
                confirmDelete.count > 1 ? "s" : ""
              }? This action cannot be undone.`
            : `Are you sure you want to delete the function "${
                confirmDelete.name || "this function"
              }"? This action cannot be undone.`
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={confirmDelete.isBulk ? confirmBulkDelete : onDeleteConfirmed}
          onCancel={() =>
            setConfirmDelete({ open: false, id: null, name: "", isBulk: false, count: 0 })
          }
          isDanger={true}
        />

        <div className="function-pagination-bottom">
          <div className="function-pagination-summary">
            Showing {showingFrom} to {showingTo} of {totalRows} entries
          </div>
          <div className="function-pagination-actions">
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
            >
              Previous
            </button>
            {visiblePageItems.map((item, index) => {
              if (item === "ellipsis") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    style={{ padding: "0 6px", color: "#6b7280" }}
                    aria-hidden="true"
                  >
                    …
                  </span>
                );
              }
              const p = Number(item);
              return (
                <button
                  key={p}
                  className={
                    p === currentPage ? "primary-btn sm" : "secondary-btn sm"
                  }
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FunctionManagementPage;

